import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobx from 'mobx';
import { FormField } from '../../../global/form/form-field/form-field';
import { SingleSelectInput } from '../../../global/single-select/single-select-input';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { AddNewEntityButton } from '../../../global/add-new-entity/add-new-entity-button';
import OptionsButton from '../../../global/options-button/options-button';
import { Permission } from '../../../../api/roles.dao';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { MORE_BUTTON_TEXT } from '../../../../constants/common-labels.constants';
import { ONLY_SPACES_REG_EXP } from '../../../../constants/regex.constants';
import { AttendeeRegistrationStatus } from '../../../../api/attendees.dao';
import { HubAttendeeFormVM } from './hub-attendee-form.viewmodel';
import {
    ORGANIZATION_TEXT,
    FIRST_NAME_TEXT,
    LAST_NAME_TEXT,
    EMAIL_TEXT,
    ASSISTANT_EMAIL_TEXT,
    TITLE_TEXT,
    SALUTATION_TEXT,
    PHONE_NUMBER_TEXT,
    ACCESS_CODE_TEXT,
    REGISTRATION_STATUS_TEXT,
    EXTERNAL_ATTENDEE_ID_TEXT,
    NO_ORGANIZATIONS_TEXT,
    NO_MATCHING_SEARCH_CRITERIA_TEXT,
} from '../../../_events/_manage-event/_attendees-list/attendees.texts';

const HubAttendeeFormPresentation = injectIntl(observer((props: { vm: HubAttendeeFormVM; intl: IntlShape }) => {
    const { vm, intl } = props;

    const hubAttendeeDeleteOptions = [
        hasPermission(Permission.DELETE_HUB_ATTENDEES) && {
            label: intl.formatMessage({
                id: 'oe.eventHubAttendees.deleteAttendee',
                defaultMessage: 'Delete Attendee',
                description: 'Delete hub attendee menu option',
            }),
            action: vm.deleteAttendee,
            danger: true,
        },
    ].filter(Boolean);
    const formData = vm.getAttendeeFormDataVM();
    const [organizationsList, setOrganizationsList] = useState([]);
    const [isOrgLoaded, setIsOrgLoaded] = useState(false);

    useEffect(() => {
        if (formData && !isOrgLoaded) {
            setOrganizationsList(() => formData.getOrganizations());
            setIsOrgLoaded(true);
        }

        if (isOrgLoaded && formData && organizationsList.length !== formData?.getOrganizations().length) {
            const newOrganizationsList = formData.getOrganizations();
            setOrganizationsList(prevOrganizationsList => {
                const addedOrg = newOrganizationsList.filter(
                    item => !prevOrganizationsList.some(elem => elem.hubOrganizationId === item.hubOrganizationId)
                );
                if (addedOrg.length) {
                    formData.setOrganizationId(addedOrg[0].hubOrganizationId);
                }
                return newOrganizationsList;
            });
        }
    }, [formData?.getOrganizations().length, organizationsList, isOrgLoaded]);

    const removeOnlySpacesFromField = mobx.action((value: string, key: string) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            formData[key] = '';
        }
    });

    if (!formData) {
        return <CommonPopupBodyFooterLayout
            body={
                <div className='oe-form'>
                    <FormattedMessage
                        description='Label text'
                        defaultMessage='Loading attendee data{suffix}'
                        id='oe.hubAttendee.loadingData'
                        values={{ suffix: '...' }}
                    />
                </div>
            }
        />;
    }

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form'>
                    <div className='oe-form__body'>
                        <FormField label={<FormattedMessage {...ORGANIZATION_TEXT} />}>
                            <SingleSelectInput
                                className='oe-form-field__control'
                                keyProperty='hubOrganizationId'
                                nameProperty='name'
                                value={formData.organizationId}
                                items={formData.getOrganizations()}
                                containerClassName='org-select-popover'
                                ActionButton={() => <AddNewEntityButton
                                    onClick={() => vm.openNewOrganizationPopup()}
                                    permission={Permission.CREATE_HUB_ORGANIZATION}
                                    label={<FormattedMessage
                                        description='Label text'
                                        defaultMessage='{prefix}New Organization'
                                        id='oe.organization.new'
                                        values={{ prefix: '+ ' }}
                                    />}
                                />}
                                onChange={mobx.action(newValue => {
                                    formData.organizationId = newValue;
                                })}
                                emptyListText={<FormattedMessage {...NO_ORGANIZATIONS_TEXT} />}
                                emptyFilterText={<FormattedMessage {...NO_MATCHING_SEARCH_CRITERIA_TEXT} />}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>
                                    {formData.getOrganizationError()}
                                </span>}
                        </FormField>
                        <div className='flex'>
                            <FormField className='m-right-8px' label={<FormattedMessage {...FIRST_NAME_TEXT} />}>
                                <input
                                    className='oe-form-field__control'
                                    value={formData.firstName || ''}
                                    onChange={mobx.action(({ target: { value } }) => { formData.firstName = value; })}
                                    onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'firstName'); })}
                                    autoFocus
                                />
                                {formData.formChecked &&
                                    <span className='oe-form-field__error'>{formData.getFirstNameError()}</span>}
                            </FormField>
                            <FormField label={<FormattedMessage {...LAST_NAME_TEXT} />}>
                                <input
                                    className='oe-form-field__control'
                                    value={formData.lastName || ''}
                                    onChange={mobx.action(({ target: { value } }) => { formData.lastName = value; })}
                                    onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'lastName'); })}
                                />
                                {formData.formChecked &&
                                    <span className='oe-form-field__error'>{formData.getLastNameError()}</span>}
                            </FormField>
                        </div>
                        <FormField label={<FormattedMessage {...EMAIL_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.email || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.clearEmailError();
                                    formData.email = value;
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, 'email');
                                    formData.isMailTouched = true;
                                })}
                            />
                            {formData.shouldShowEmailValidationError(formData.email, true, true) &&
                                <span className='oe-form-field__error'>
                                    {formData.getEmailError(formData.email, true) || formData.getFieldError('email')}
                                </span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...ASSISTANT_EMAIL_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.assistantEmail || ''}
                                onChange={mobx.action(({ target: { value } }) => { formData.assistantEmail = value; })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'assistantEmail'); })}
                            />
                            {formData.shouldShowEmailValidationError(formData.assistantEmail, false) &&
                                <span className='oe-form-field__error'>
                                    {formData.getEmailError(formData.assistantEmail)}
                                </span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...TITLE_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.title || ''}
                                onChange={mobx.action(({ target: { value } }) => { formData.title = value; })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'title'); })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getTitleError()}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...SALUTATION_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.salutation || ''}
                                onChange={mobx.action(({ target: { value } }) => { formData.salutation = value; })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'salutation'); })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getSalutationError()}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...PHONE_NUMBER_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.phoneNumber || ''}
                                onChange={mobx.action(({ target: { value } }) => { formData.phoneNumber = value; })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'phoneNumber'); })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getPhoneNumberError()}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...ACCESS_CODE_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.accessCode || ''}
                                onChange={mobx.action(({ target: { value } }) => { formData.accessCode = value; })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'accessCode'); })}
                            />
                            {formData.formChecked &&
                                <span className='oe-form-field__error'>{formData.getAccessCodeError()}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage {...REGISTRATION_STATUS_TEXT} />} className='w-50'>
                            <select
                                value={formData.registrationStatus}
                                onChange={mobx.action(e => formData.registrationStatus = e.target.value as AttendeeRegistrationStatus)}
                                className='oe-form-field__control'
                            >
                                {Object.keys(AttendeeRegistrationStatus).map((item: string) =>
                                    <option key={item} value={AttendeeRegistrationStatus[item]}>
                                        {intl.formatMessage({ id: `oe.registrationStatus.${item}` })}
                                    </option>
                                )}
                            </select>
                        </FormField>
                        <FormField label={<FormattedMessage {...EXTERNAL_ATTENDEE_ID_TEXT} />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.externalAttendeeId || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.clearExternalAttendeeIdError();
                                    formData.externalAttendeeId = value;
                                })}
                                onBlur={mobx.action(({ target: { value } }) => { removeOnlySpacesFromField(value, 'externalAttendeeId'); })}
                            />
                            {formData.getExternalAttendeeIdError() &&
                                <span className='oe-form-field__error'>
                                    {formData.getExternalAttendeeIdError()}
                                </span>}
                        </FormField>
                    </div>
                    {formData.getServerErrorMessage() &&
                        <div className='oe-form-field__error'>{formData.getServerErrorMessage()}</div>}
                </form>
            }
            footer={
                <>
                    {vm.isSaving && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.hubAttendee.attendeeDataIsLoading'
                                description='Preloader message text'
                                defaultMessage='Attendee data is loading, please wait{suffix}'
                                values={{ suffix: '...' }}
                            />
                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={vm.isEditMode() ? 'Apply' : 'Add'}
                            modifier={['fat', 'confirm-safe']}
                            onClick={vm.formSubmitAction}
                            disabled={vm.isSaving}
                        />
                        <DefaultCloseSubWindowButton text='Cancel' />
                        {vm.getAttendeeId() && hubAttendeeDeleteOptions.length > 0 && (
                            <OptionsButton
                                className='options-button--right-margin-auto'
                                modifier={['fat', 'options']}
                                label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                                options={hubAttendeeDeleteOptions}
                                position='top'
                                align='start'
                            />
                        )}
                    </div>
                </>
            }
        />
    );
}));

export { HubAttendeeFormPresentation };
